import { Injectable } from '@angular/core';
import Dexie, { Table } from 'dexie';
import { KnowledgeUserState, ConceptStatus } from '../models/knowledge.models';

@Injectable({
  providedIn: 'root',
})
export class KnowledgeDbService extends Dexie {
  knowledgeUserStateTable!: Table<KnowledgeUserState, string>;

  constructor() {
    super('KnowledgeStateDB');
    this.version(1).stores({
      knowledgeState: 'concept, conceptPath',
    });
    this.knowledgeUserStateTable = this.table('knowledgeState');
  }

  async getKnowledgeUserState(concept: string): Promise<KnowledgeUserState | undefined> {
    return this.knowledgeUserStateTable.get(concept);
  }

  async getAllKnowledgeUserStates(): Promise<KnowledgeUserState[]> {
    return this.knowledgeUserStateTable.toArray();
  }

  async getAllKnowledgeUserStateAsObject(): Promise<{ [concept: string]: KnowledgeUserState }> {
    const statesArray = await this.knowledgeUserStateTable.toArray();
    return statesArray.reduce((acc, state) => {
      acc[state.concept] = state;
      return acc;
    }, {} as { [concept: string]: KnowledgeUserState });
  }

  async addOrUpdateKnowledgeUserState(state: KnowledgeUserState): Promise<string> {
    // Dexie's put method returns the primary key of the written object.
    // Since 'state.concept' is our primary key, it will be returned.
    return this.knowledgeUserStateTable.put(state);
  }

  async bulkAddOrUpdateKnowledgeUserStates(states: KnowledgeUserState[]): Promise<string[]> {
    // Dexie's bulkPut for tables with inbound keys (like 'concept' on KnowledgeUserState)
    // will return an array of these keys upon successful completion.

    const result = await this.knowledgeUserStateTable.bulkPut(states);
    console.log(result);
    return states.map((s) => s.concept); // Return the concepts as per previous behavior
  }

  async deleteKnowledgeUserState(concept: string): Promise<void> {
    return this.knowledgeUserStateTable.delete(concept);
  }

  async clearAllData(): Promise<void> {
    await this.knowledgeUserStateTable.clear();
  }

  // Example of how to initialize or get a default state
  async getOrCreateKnowledgeUserState(
    conceptId: string,
    concept: string,
    conceptType: string,
    defaultStatus: ConceptStatus = ConceptStatus.NotEncountered,
  ): Promise<KnowledgeUserState> {
    let userState = await this.getKnowledgeUserState(concept);
    if (!userState) {
      userState = {
        conceptId,
        concept,
        conceptType,
        conceptPath: '',
        status: defaultStatus,
        stats: {
          totalReviews: 0,
          successCount: 0,
          failedCount: 0,
          totalTimeSpent: 0,
          progress: 0,
        },
        firstTimeEncountered: defaultStatus !== ConceptStatus.NotEncountered ? new Date() : undefined,
      };
      await this.addOrUpdateKnowledgeUserState(userState);
    }
    return userState;
  }
}
